// Stock Transfers - Step 2: Configure Transfers
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Info,
  Save,
  Trash2,
  Plus,
  Package,
  MapPin,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { TransferSetupData } from "./stock-transfers-step1";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { mockStockItems, StockItem } from "@/data/mockStockItems";

// Color palette for items - using global CSS colors
const ITEM_COLORS = [
  { icon: "text-purple-500", badgeBg: "bg-purple-100 dark:bg-purple-900/30", badgeText: "text-purple-700 dark:text-purple-300" },
  { icon: "text-info-500", badgeBg: "bg-info-100 dark:bg-info-900/30", badgeText: "text-info-700 dark:text-info-300" },
  { icon: "text-success-500", badgeBg: "bg-success-100 dark:bg-success-900/30", badgeText: "text-success-700 dark:text-success-300" },
  { icon: "text-cyan-500", badgeBg: "bg-cyan-100 dark:bg-cyan-900/30", badgeText: "text-cyan-700 dark:text-cyan-300" },
  { icon: "text-indigo-500", badgeBg: "bg-indigo-100 dark:bg-indigo-900/30", badgeText: "text-indigo-700 dark:text-indigo-300" },
  { icon: "text-emerald-500", badgeBg: "bg-emerald-100 dark:bg-emerald-900/30", badgeText: "text-emerald-700 dark:text-emerald-300" },
  { icon: "text-burntorange-500", badgeBg: "bg-burntorange-100 dark:bg-burntorange-900/30", badgeText: "text-burntorange-700 dark:text-burntorange-300" },
  { icon: "text-electricviolet-500", badgeBg: "bg-electricviolet-100 dark:bg-electricviolet-900/30", badgeText: "text-electricviolet-700 dark:text-electricviolet-300" },
  { icon: "text-blueviolet-500", badgeBg: "bg-blueviolet-100 dark:bg-blueviolet-900/30", badgeText: "text-blueviolet-700 dark:text-blueviolet-300" },
  { icon: "text-sapgreen-500", badgeBg: "bg-sapgreen-100 dark:bg-sapgreen-900/30", badgeText: "text-sapgreen-700 dark:text-sapgreen-300" },
];

// Helper function to get color for an item based on its item code (consistent across all transfers of same item)
const getItemColor = (itemCode: string, allTransfers: TransferItem[]) => {
  // Get unique item codes in the order they appear
  const uniqueItemCodes = Array.from(new Set(allTransfers.map(t => t.itemCode)));
  const itemIndex = uniqueItemCodes.indexOf(itemCode);
  return ITEM_COLORS[itemIndex % ITEM_COLORS.length];
};
import { AddItemsDialog } from "@/components/AddItemsDialog";

export interface DistributionDestination {
  destinationLocation: string;
  quantity: number;
}

export interface TransferItem {
  itemId: string;
  itemCode: string;
  itemName: string;
  availableQuantity: number;
  sourceLocation: string;
  destinationLocation: string;
  quantity: number;
  splitIndex?: number; // Track if this is a split transfer (1, 2, 3, etc.)
  originalItemCode?: string; // Track the original item code for grouping
  distributions?: DistributionDestination[]; // Multiple destinations for distribution
  color?: { icon: string; badgeBg: string; badgeText: string }; // Color for visual consistency
}

export interface TransferConfigData extends TransferSetupData {
  transfers: TransferItem[];
}

interface StockTransfersStep2PageProps {
  pageParams?: {
    setupData?: TransferSetupData;
    configData?: TransferConfigData;
    isEditing?: boolean;
    transferId?: string;
  };
}

export function StockTransfersStep2Page({ pageParams }: StockTransfersStep2PageProps) {
  const { navigateTo } = useNavigation();
  const setupData = pageParams?.setupData;
  const configData = pageParams?.configData;
  const isEditing = pageParams?.isEditing || false;
  const transferId = pageParams?.transferId;

  // Check if we're in "Transfer by Location" mode
  const isLocationMode = setupData?.transferMethod === "location";
  const prefilledSourceLocation = setupData?.sourceLocation || "";

  const [defaultDestination, setDefaultDestination] = useState("");
  const [transfers, setTransfers] = useState<TransferItem[]>([]);
  const [addItemsDialogOpen, setAddItemsDialogOpen] = useState(false);

  // Initialize transfers from selected items OR from existing config data
  useEffect(() => {
    console.log('🔍 [Step 2] Initializing transfers');
    console.log('🔍 [Step 2] Is location mode:', isLocationMode);
    console.log('🔍 [Step 2] Prefilled source location:', prefilledSourceLocation);
    
    if (isEditing && configData?.transfers) {
      // If editing, load the existing transfers
      setTransfers(configData.transfers);
    } else if (setupData?.selectedItems) {
      // If creating new, initialize from selected items
      // Apply pre-filled source location if in location mode
      const newTransfers = setupData.selectedItems.map((item, index) => ({
        itemId: item.id,
        itemCode: item.code,
        itemName: item.name,
        availableQuantity: item.availableQuantity,
        sourceLocation: isLocationMode ? prefilledSourceLocation : "", // Pre-fill in location mode
        destinationLocation: "",
        quantity: 0,
        color: ITEM_COLORS[index % ITEM_COLORS.length], // Assign color based on order
      }));
      
      console.log('📦 [Step 2] Created transfers with source location:', isLocationMode ? prefilledSourceLocation : "");
      console.log('📦 [Step 2] Transfers array:', newTransfers);
      
      setTransfers(newTransfers);
      
      // Show toast notification if in location mode
      if (isLocationMode && prefilledSourceLocation) {
        toast.success(`Source location pre-filled: ${prefilledSourceLocation}`);
      }
    }
  }, [setupData, configData, isEditing, isLocationMode, prefilledSourceLocation]);



  // Redirect if no setup data
  useEffect(() => {
    if (!setupData) {
      toast.error("Please complete step 1 first");
      navigateTo("stock-transfers/create/step1");
    }
  }, [setupData, navigateTo]);

  if (!setupData) {
    return null;
  }

  // Get all locations and map them with warehouse names
  const allLocations = mockLocations
    .filter((location) => location.active && location.status === "Active")
    .map((location) => {
      const warehouse = mockWarehouses.find((w) => w.code === location.warehouseCode);
      return {
        value: location.locationCode,
        label: `${warehouse?.name || location.warehouseCode} - ${location.locationCode}`,
        warehouseCode: location.warehouseCode,
        locationCode: location.locationCode,
      };
    });

  // Helper function to get locations where a specific stock item exists
  const getLocationsForStockItem = (stockCode: string) => {
    const stockItem = mockStockItems.find((item) => item.code === stockCode);
    
    if (!stockItem) return allLocations;

    // If the item has locationQuantities, filter to only those locations
    if (stockItem.locationQuantities && stockItem.locationQuantities.length > 0) {
      const itemLocationCodes = stockItem.locationQuantities
        .filter((lq) => lq.quantity > 0) // Only locations with available stock
        .map((lq) => lq.locationCode);
      
      return allLocations.filter((loc) => itemLocationCodes.includes(loc.locationCode));
    }

    // Fallback: if no locationQuantities, show only the primary location
    return allLocations.filter(
      (loc) => 
        loc.warehouseCode === stockItem.warehouseCode && 
        loc.locationCode === stockItem.locationCode
    );
  };

  // Helper function to get quantity available at a specific location for a stock item
  const getQuantityAtLocation = (stockCode: string, locationCode: string): number => {
    const stockItem = mockStockItems.find((item) => item.code === stockCode);
    
    if (!stockItem) return 0;

    // If the item has locationQuantities, find the specific location
    if (stockItem.locationQuantities && stockItem.locationQuantities.length > 0) {
      const locationQty = stockItem.locationQuantities.find((lq) => lq.locationCode === locationCode);
      return locationQty?.quantity || 0;
    }

    // Fallback: if no locationQuantities and this is the primary location, return total quantity
    if (stockItem.locationCode === locationCode) {
      return stockItem.physicalQuantity;
    }

    return 0;
  };

  const handleApplyDestinationToAll = () => {
    if (!defaultDestination) {
      toast.error("Please select a default destination location");
      return;
    }
    setTransfers(
      transfers.map((t) => ({
        ...t,
        destinationLocation: defaultDestination,
      }))
    );
    toast.success("Destination location applied to all items");
  };

  const handleUpdateTransfer = (index: number, field: keyof TransferItem, value: any) => {
    const updated = [...transfers];
    updated[index] = { ...updated[index], [field]: value };
    setTransfers(updated);
  };

  const handleRemoveTransfer = (index: number) => {
    setTransfers(transfers.filter((_, i) => i !== index));
    toast.success("Item removed from transfer");
  };

  const handleSplitTransfer = (itemCode: string) => {
    // Find all existing transfers for this item code
    const existingTransfers = transfers.filter(
      (t) => t.itemCode === itemCode || t.originalItemCode === itemCode
    );
    
    // Get the original transfer (the one without splitIndex or with splitIndex 1)
    const originalTransfer = existingTransfers.find((t) => !t.splitIndex || t.splitIndex === 1) || existingTransfers[0];
    
    // Count existing splits to determine the new split index
    const splitCount = existingTransfers.length;
    
    // Create a new transfer with the same item but empty source
    const newTransfer: TransferItem = {
      itemId: `${originalTransfer.itemId}-split-${splitCount + 1}`,
      itemCode: originalTransfer.itemCode,
      itemName: originalTransfer.itemName,
      availableQuantity: originalTransfer.availableQuantity,
      sourceLocation: "", // Empty - user needs to select
      destinationLocation: originalTransfer.destinationLocation, // Copy destination as default
      quantity: 0,
      splitIndex: splitCount + 1,
      originalItemCode: originalTransfer.itemCode,
      color: originalTransfer.color, // Preserve the color
    };
    
    // Update the original transfer to have splitIndex 1 if it doesn't have one
    const updatedTransfers = transfers.map((t) => {
      if (t.itemCode === itemCode && !t.splitIndex) {
        return { ...t, splitIndex: 1, originalItemCode: itemCode };
      }
      return t;
    });
    
    // Add the new transfer after the last transfer of this item
    const lastIndex = updatedTransfers.map((t, i) => ({ t, i }))
      .filter(({ t }) => t.itemCode === itemCode || t.originalItemCode === itemCode)
      .pop()?.i || 0;
    
    updatedTransfers.splice(lastIndex + 1, 0, newTransfer);
    
    setTransfers(updatedTransfers);
    toast.success(`Added another transfer for ${itemCode} from a different location`);
  };

  const handleDistributeTransfer = (index: number) => {
    const transfer = transfers[index];
    
    // Initialize distributions array if it doesn't exist
    if (!transfer.distributions) {
      transfer.distributions = [];
    }
    
    // Add a new distribution destination
    const updatedTransfers = [...transfers];
    updatedTransfers[index] = {
      ...transfer,
      distributions: [
        ...transfer.distributions,
        { destinationLocation: "", quantity: 0 }
      ]
    };
    
    setTransfers(updatedTransfers);
    toast.success(`Added another destination for ${transfer.itemCode}`);
  };

  const handleUpdateDistribution = (
    transferIndex: number,
    distributionIndex: number,
    field: keyof DistributionDestination,
    value: any
  ) => {
    const updatedTransfers = [...transfers];
    const transfer = updatedTransfers[transferIndex];
    
    if (transfer.distributions) {
      // If updating quantity, validate against available stock
      if (field === "quantity" && transfer.sourceLocation) {
        const availableAtLocation = getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation);
        
        // Calculate total quantity from other distributions
        const otherDistributionsTotal = transfer.distributions.reduce((sum, dist, idx) => {
          if (idx !== distributionIndex) {
            return sum + (dist.quantity || 0);
          }
          return sum;
        }, 0);
        
        // Check if new value would exceed available
        const newTotal = otherDistributionsTotal + (value || 0);
        if (newTotal > availableAtLocation) {
          toast.error(`Total distributed quantity cannot exceed ${availableAtLocation} units available at source location`);
          return;
        }
      }
      
      transfer.distributions[distributionIndex] = {
        ...transfer.distributions[distributionIndex],
        [field]: value
      };
      setTransfers(updatedTransfers);
    }
  };

  const handleRemoveDistribution = (transferIndex: number, distributionIndex: number) => {
    const updatedTransfers = [...transfers];
    const transfer = updatedTransfers[transferIndex];
    
    if (transfer.distributions) {
      transfer.distributions.splice(distributionIndex, 1);
      setTransfers(updatedTransfers);
      toast.success("Distribution destination removed");
    }
  };

  const handleAddMoreItems = () => {
    setAddItemsDialogOpen(true);
  };

  const handleAddItems = (items: StockItem[]) => {
    const startIndex = transfers.length; // Start color index from current length
    const newTransfers: TransferItem[] = items.map((item, index) => ({
      itemId: item.id,
      itemCode: item.code,
      itemName: item.description,
      availableQuantity: item.physicalQuantity,
      sourceLocation: "",
      destinationLocation: "",
      quantity: 0,
      color: ITEM_COLORS[(startIndex + index) % ITEM_COLORS.length], // Assign color
    }));

    setTransfers([...transfers, ...newTransfers]);
    toast.success(`Added ${items.length} item(s) to transfer`);
  };

  const handleBack = () => {
    navigateTo("stock-transfers/create/step1", { setupData });
  };

  const handleNext = () => {
    // Validation
    const errors: string[] = [];

    transfers.forEach((transfer, index) => {
      if (!transfer.sourceLocation) {
        errors.push(`Item ${index + 1} (${transfer.itemCode}): Source location is required`);
      }
      
      // Check if there are distributions
      if (transfer.distributions && transfer.distributions.length > 0) {
        // Validate each distribution
        transfer.distributions.forEach((dist, distIndex) => {
          if (!dist.destinationLocation) {
            errors.push(`Item ${index + 1} (${transfer.itemCode}), Distribution ${distIndex + 1}: Destination location is required`);
          }
          if (transfer.sourceLocation === dist.destinationLocation) {
            errors.push(`Item ${index + 1} (${transfer.itemCode}), Distribution ${distIndex + 1}: Source and destination cannot be the same`);
          }
          if (!dist.quantity || dist.quantity <= 0) {
            errors.push(`Item ${index + 1} (${transfer.itemCode}), Distribution ${distIndex + 1}: Quantity must be greater than 0`);
          }
        });
        
        // Check total distributed quantity doesn't exceed available
        const totalDistributed = transfer.distributions.reduce((sum, d) => sum + d.quantity, 0);
        const availableAtLocation = getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation);
        if (totalDistributed > availableAtLocation) {
          errors.push(`Item ${index + 1} (${transfer.itemCode}): Total distributed quantity (${totalDistributed}) exceeds available stock at source location (${availableAtLocation})`);
        }
      } else {
        // Single destination validation
        if (!transfer.destinationLocation) {
          errors.push(`Item ${index + 1} (${transfer.itemCode}): Destination location is required`);
        }
        if (transfer.sourceLocation === transfer.destinationLocation) {
          errors.push(`Item ${index + 1} (${transfer.itemCode}): Source and destination cannot be the same`);
        }
        if (!transfer.quantity || transfer.quantity <= 0) {
          errors.push(`Item ${index + 1} (${transfer.itemCode}): Quantity must be greater than 0`);
        }
        // Check quantity against the actual available quantity at the source location
        if (transfer.sourceLocation) {
          const availableAtLocation = getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation);
          if (transfer.quantity > availableAtLocation) {
            errors.push(`Item ${index + 1} (${transfer.itemCode}): Quantity (${transfer.quantity}) exceeds available stock at source location (${availableAtLocation})`);
          }
        }
      }
    });

    if (transfers.length === 0) {
      errors.push("At least one transfer is required");
    }

    if (errors.length > 0) {
      toast.error(
        <div>
          <div className="font-semibold mb-1">Please fix the following errors:</div>
          <ul className="list-disc list-inside space-y-1">
            {errors.slice(0, 3).map((error, i) => (
              <li key={i} className="text-sm">{error}</li>
            ))}
            {errors.length > 3 && (
              <li className="text-sm">...and {errors.length - 3} more</li>
            )}
          </ul>
        </div>
      );
      return;
    }

    // Flatten distributions into separate transfer objects for Step 3
    const flattenedTransfers: TransferItem[] = [];
    
    transfers.forEach((transfer) => {
      if (transfer.distributions && transfer.distributions.length > 0) {
        // If item has distributions, create separate transfer objects for each destination
        transfer.distributions.forEach((dist) => {
          flattenedTransfers.push({
            itemId: transfer.itemId,
            itemCode: transfer.itemCode,
            itemName: transfer.itemName,
            availableQuantity: transfer.availableQuantity,
            sourceLocation: transfer.sourceLocation,
            destinationLocation: dist.destinationLocation,
            quantity: dist.quantity,
            color: transfer.color,
            originalItemCode: transfer.itemCode, // Track original item for grouping
          });
        });
      } else {
        // Single destination transfer
        flattenedTransfers.push({
          ...transfer,
          originalItemCode: transfer.itemCode, // Track original item for grouping
        });
      }
    });

    // Navigate to step 3 with flattened data
    const configData: TransferConfigData = {
      ...setupData,
      transfers: flattenedTransfers,
    };
    navigateTo("stock-transfers/create/step3", { 
      configData,
      isEditing,
      transferId,
    });
  };

  const handleSaveAndExit = () => {
    // TODO: Save as draft
    toast.success("Transfer saved as draft! You can resume it later from the Stock Transfers page.");
    navigateTo("stock-transfers");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-4">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 flex items-center justify-between pb-4 border-b bg-background px-4 pt-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Configure transfer details for each item
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto py-6">
        <div className="max-w-6xl mx-auto w-full space-y-6">
          {/* Progress Indicator */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
                  ✓
                </div>
                <span className="text-sm text-muted-foreground">Select Items</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
                  2
                </div>
                <span className="text-sm font-medium">Configure Transfers</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
                  3
                </div>
                <span className="text-sm text-muted-foreground">Review & Complete</span>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Configure transfer details
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Set the source location, destination location, and quantity for each item. Use the bulk actions to quickly apply the same locations to all items.
              </p>
            </div>
          </div>
        </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="mb-6">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Set a common destination for all transfers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 max-w-md">
            <div className="flex-1 space-y-2">
              <Label htmlFor="default-destination">Default Destination Location</Label>
              <Select value={defaultDestination} onValueChange={setDefaultDestination}>
                <SelectTrigger id="default-destination">
                  <SelectValue placeholder="Select destination..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {allLocations.map((location) => (
                    <SelectItem key={location.value} value={location.value}>
                      {location.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyDestinationToAll} variant="secondary">
              Apply to All
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            💡 Tip: Useful when consolidating multiple items to one location
          </p>
        </CardContent>
          </Card>

          {/* Transfer Items */}
          <Card className="flex-1">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transfer Items</CardTitle>
              <CardDescription>
                Configure each transfer ({transfers.length} items)
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleAddMoreItems}>
              <Plus className="h-4 w-4 mr-2" />
              Add More Items
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transfers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No items to transfer. Click "Add More Items" to select items.
              </div>
            ) : (
              transfers.map((transfer, index) => {
                const itemColor = transfer.color || ITEM_COLORS[0]; // Use stored color or fallback
                return (
                <Card key={transfer.itemId} className="border-2">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Item Header - Compact Layout */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <Package className={`h-5 w-5 mt-0.5 ${itemColor.icon}`} />
                          <div className="flex-1 min-w-0">
                            {/* Code and Name on same line */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-semibold">{transfer.itemCode}</div>
                              <span className="text-muted-foreground">•</span>
                              <div className="text-sm text-muted-foreground truncate">
                                {transfer.itemName}
                              </div>
                              {transfer.splitIndex && (
                                <Badge className={`text-xs ${itemColor.badgeBg} ${itemColor.badgeText} border-0`}>
                                  Transfer {transfer.splitIndex}
                                </Badge>
                              )}
                            </div>
                            {/* Available quantity badge on same line or next line */}
                            <div className="mt-1">
                              <Badge variant="outline" className="text-xs">
                                {transfer.sourceLocation 
                                  ? `Available at selected location: ${getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation)}` 
                                  : `Total available: ${transfer.availableQuantity}`}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveTransfer(index)}
                          className="flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>

                      {/* Transfer Configuration */}
                      <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
                        {/* Source Location - Left Column */}
                        <div className="space-y-2">
                          <Label htmlFor={`source-${index}`}>
                            <MapPin className="h-4 w-4 inline mr-1" />
                            From Location *
                            {isLocationMode && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                Pre-filled
                              </Badge>
                            )}
                          </Label>
                          <Select
                            key={`source-${index}-${transfer.sourceLocation}`}
                            value={transfer.sourceLocation || ""}
                            onValueChange={(value) =>
                              handleUpdateTransfer(index, "sourceLocation", value)
                            }
                            disabled={isLocationMode} // Disable in location mode
                          >
                            <SelectTrigger id={`source-${index}`}>
                              <SelectValue placeholder="Select source..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {getLocationsForStockItem(transfer.itemCode).map((location) => {
                                const qtyAtLocation = getQuantityAtLocation(transfer.itemCode, location.locationCode);
                                return (
                                  <SelectItem key={location.value} value={location.value}>
                                    {location.label} ({qtyAtLocation} available)
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {transfer.sourceLocation && (
                            <p className="text-xs text-muted-foreground">
                              Available: {getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation)} units
                            </p>
                          )}
                        </div>

                        {/* Destination(s) - Middle Column */}
                        <div className="space-y-2">
                          <Label>
                            <MapPin className="h-4 w-4 inline mr-1" />
                            To Location *
                          </Label>
                          {transfer.distributions && transfer.distributions.length > 0 ? (
                            // Multiple distributions
                            <div className="space-y-2">
                              {transfer.distributions.map((dist, distIndex) => (
                                <Select
                                  key={distIndex}
                                  value={dist.destinationLocation}
                                  onValueChange={(value) =>
                                    handleUpdateDistribution(index, distIndex, "destinationLocation", value)
                                  }
                                >
                                  <SelectTrigger id={`destination-${index}-${distIndex}`}>
                                    <SelectValue placeholder="Select destination..." />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[300px]">
                                    {allLocations.map((location) => (
                                      <SelectItem key={location.value} value={location.value}>
                                        {location.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ))}
                            </div>
                          ) : (
                            // Single destination
                            <Select
                              value={transfer.destinationLocation}
                              onValueChange={(value) =>
                                handleUpdateTransfer(index, "destinationLocation", value)
                              }
                            >
                              <SelectTrigger id={`destination-${index}`}>
                                <SelectValue placeholder="Select destination..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-[300px]">
                                {allLocations.map((location) => (
                                  <SelectItem key={location.value} value={location.value}>
                                    {location.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        {/* Quantity - Right Column (with delete button) */}
                        <div className="space-y-2" style={{ width: '180px' }}>
                          <Label>Quantity *</Label>
                          {transfer.distributions && transfer.distributions.length > 0 ? (
                            // Multiple distributions
                            <div className="space-y-2">
                              {transfer.distributions.map((dist, distIndex) => (
                                <div key={distIndex} className="flex items-center gap-2">
                                  <Input
                                    id={`dist-quantity-${index}-${distIndex}`}
                                    type="number"
                                    min="1"
                                    max={transfer.sourceLocation ? getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation) : transfer.availableQuantity}
                                    value={dist.quantity || ""}
                                    onChange={(e) =>
                                      handleUpdateDistribution(
                                        index,
                                        distIndex,
                                        "quantity",
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    placeholder="0"
                                    className="flex-1"
                                  />
                                  {transfer.distributions && transfer.distributions.length > 1 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemoveDistribution(index, distIndex)}
                                      className="h-10 w-10 p-0 flex-shrink-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              {transfer.sourceLocation && (
                                <p className="text-xs text-muted-foreground">
                                  Total: {transfer.distributions.reduce((sum, d) => sum + d.quantity, 0)} / {getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation)} available
                                </p>
                              )}
                            </div>
                          ) : (
                            // Single quantity
                            <>
                              <Input
                                id={`quantity-${index}`}
                                type="number"
                                min="1"
                                max={transfer.sourceLocation ? getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation) : transfer.availableQuantity}
                                value={transfer.quantity || ""}
                                onChange={(e) =>
                                  handleUpdateTransfer(
                                    index,
                                    "quantity",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                placeholder="0"
                              />
                              {transfer.sourceLocation ? (
                                <p className="text-xs text-muted-foreground">
                                  Max: {getQuantityAtLocation(transfer.itemCode, transfer.sourceLocation)} units
                                </p>
                              ) : (
                                <p className="text-xs text-muted-foreground">
                                  Max: {transfer.availableQuantity} units
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Split/Distribute Transfer Buttons */}
                      <div className="pt-4 border-t space-y-3">
                        <div className={`grid grid-cols-1 ${isLocationMode ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-3`}>
                          {!isLocationMode && (
                            <div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSplitTransfer(transfer.itemCode)}
                                className="w-full"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Transfer from Another Location
                              </Button>
                              <p className="text-xs text-muted-foreground text-center mt-1.5">
                                Consolidate from multiple sources
                              </p>
                            </div>
                          )}
                          <div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDistributeTransfer(index)}
                              className="w-full"
                              disabled={!transfer.sourceLocation}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Distribute to Another Location
                            </Button>
                            <p className="text-xs text-muted-foreground text-center mt-1.5">
                              {transfer.sourceLocation 
                                ? "Send to multiple destinations" 
                                : "Select source location first"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
              })
            )}
          </div>
        </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Action Bar */}
      <div className="flex-shrink-0 border-t bg-background shadow-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSaveAndExit}>
              <Save className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
            <Button onClick={handleNext} size="lg">
              Next: Review & Complete
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Add Items Dialog */}
      <AddItemsDialog
        open={addItemsDialogOpen}
        onOpenChange={setAddItemsDialogOpen}
        selectedItemCodes={transfers.map((t) => t.itemCode)}
        allItems={mockStockItems}
        onAddItems={handleAddItems}
      />
    </div>
  );
}
